import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
    IconSearch,
    IconAudit,
    IconIdea,
    IconBuild,
    IconFlaskCurrentColor,
    IconScale
} from '../sprint-icons';

export default function CroStepper() {
    const [activeStep, setActiveStep] = useState(0);

    const nextStep = () => {
        if (activeStep < 5) setActiveStep(activeStep + 1);
    };
    const prevStep = () => {
        if (activeStep > 0) setActiveStep(activeStep - 1);
    };

    const stepsData = [
        {
            num: 1,
            title: 'Research',
            longTitle: 'Analytics & Journey Research',
            desc: 'Deep-dive into your analytics, user recordings, and competitive landscape. We map the complete customer journey to uncover where users struggle.',
            icon: <IconSearch />,
            deliverables: ['Google Analytics 4 deep-dive', 'Hotjar/Clarity screen recordings', 'User persona & journey mapping']
        },
        {
            num: 2,
            title: 'Audit',
            longTitle: 'Friction Point Audit',
            desc: 'Identify every friction point and drop-off using heatmaps, scroll maps, and checkout funnel analysis. We analyze why visitors leave before buying.',
            icon: <IconAudit />,
            deliverables: ['Heatmap & click map analysis', 'Checkout funnel drop-off audit', 'Mobile responsiveness audit']
        },
        {
            num: 3,
            title: 'Hypothesize',
            longTitle: 'Hypotheses & Prioritization',
            desc: 'Create data-backed test ideas scored using the ICE framework (Impact, Confidence, Ease). We prioritize tests that will move the needle the most.',
            icon: <IconIdea />,
            deliverables: ['ICE test prioritization sheets', 'A/B test hypotheses formulation', '12-month experimentation roadmap']
        },
        {
            num: 4,
            title: 'Build',
            longTitle: 'Design & Code Variants',
            desc: 'Design and develop improved variants — new layouts, copy, call-to-actions, and checkout flows — all informed by our research insights.',
            icon: <IconBuild />,
            deliverables: ['High-converting wireframes', 'Persuasive copywriting variations', 'Responsive variant development']
        },
        {
            num: 5,
            title: 'Test',
            longTitle: 'Run A/B Experiments',
            desc: 'Launch and run statistically significant A/B experiments via VWO, Optimizely, or custom routing. We monitor tests to ensure data integrity.',
            icon: <IconFlaskCurrentColor />,
            deliverables: ['A/B test configuration & setup', 'Quality assurance checking', 'Split traffic monitoring']
        },
        {
            num: 6,
            title: 'Scale',
            longTitle: 'Scale & Loop Learnings',
            desc: 'Implement winning experiences site-wide and document key learnings. We loop these insights back into the next optimization cycle.',
            icon: <IconScale />,
            deliverables: ['Winner deployment coordination', 'Statistical significance validation', 'Knowledge base & insights archiving']
        }
    ];

    return (
        <section className="cro-process">
            <Container>
                <div className="section-header cro-reveal">
                    <span className="cro-section-label">How It Works</span>
                    <h2 className="cro-section-title">
                        Our 6-step <span className="highlight">CRO framework</span>
                    </h2>
                </div>

                <div className="cro-process-stepper-nav cro-reveal">
                    <div className="cro-process-line-bg">
                        <div
                            className="cro-process-line-progress"
                            style={{ width: `${(activeStep / (stepsData.length - 1)) * 100}%` }}
                        ></div>
                    </div>
                    <div className="cro-process-nav-items">
                        {stepsData.map((step, idx) => {
                            const isActive = idx === activeStep;
                            const isCompleted = idx < activeStep;
                            return (
                                <button
                                    key={idx}
                                    className={`cro-process-nav-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                    onClick={() => setActiveStep(idx)}
                                    aria-label={`Go to step ${step.num}: ${step.title}`}
                                >
                                    <div className="nav-circle">
                                        {isCompleted ? '✓' : step.num}
                                    </div>
                                    <span className="nav-label">{step.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="cro-process-panel cro-reveal">
                    <div className="cro-process-panel-content" key={activeStep}>
                        <Row className="align-items-center">
                            <Col md={5} className="cro-process-visual-col">
                                <div className="step-visual-badge-wrap">
                                    <div className="step-icon-glowing-circle">
                                        {stepsData[activeStep].icon}
                                    </div>
                                </div>
                                <div className="step-deliverables">
                                    <h5>Key Deliverables</h5>
                                    <ul>
                                        {stepsData[activeStep].deliverables.map((item, idx) => (
                                            <li key={idx}>
                                                <span className="bullet-check">✓</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Col>
                            <Col md={7} className="cro-process-desc-col">
                                <span className="step-indicator-tag">Step {stepsData[activeStep].num} of 6</span>
                                <h3>{stepsData[activeStep].longTitle}</h3>
                                <p className="step-description">{stepsData[activeStep].desc}</p>

                                <div className="cro-step-actions">
                                    <button
                                        className="cro-btn-nav-prev"
                                        onClick={prevStep}
                                        disabled={activeStep === 0}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle', marginTop: '-2px' }}><path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg> Previous Step
                                    </button>
                                    {activeStep === 5 ? (
                                        <Link to="/book-audit" className="cro-btn-nav-next final-cta-btn">
                                            Book Free CRO Audit <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '8px', verticalAlign: 'middle', marginTop: '-2px' }}><path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </Link>
                                    ) : (
                                        <button
                                            className="cro-btn-nav-next"
                                            onClick={nextStep}
                                        >
                                            Next Step <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '8px', verticalAlign: 'middle', marginTop: '-2px' }}><path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
            </Container>
        </section>
    );
}

